import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  studentFormsApi,
  type StudentForm,
  type StudentFormAnswerValue,
} from '@/api/studentForms'
import {
  GOALS_WITH_DEADLINE,
  LEARNING_GOALS,
  languageProfileApi,
  type LearningGoal,
  type StudentLanguageProfile,
} from '@/api/placement'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { StudentFormFieldInput } from './StudentFormFieldInput'
import { StudentFormHistoryDialog } from './StudentFormHistoryDialog'

interface StudentFormEditDialogProps {
  open: boolean
  contractId: number
  /** Владелец профиля: цель и расписание живут в нём, а не в договоре. */
  userId: number
  /** Уже загруженная анкета — модалка открывается сразу, без ожидания запроса. */
  form: StudentForm | null
  /** Языковой профиль: цель, срок и желаемое расписание. */
  profile: StudentLanguageProfile | null
  /** Учитель ведёт анкету: правит все поля и смотрит историю правок. */
  canManage: boolean
  onClose: () => void
  onSaved: (result: { form?: StudentForm; profile?: StudentLanguageProfile }) => void
}

/** Цель и расписание: числа держим строками, как их отдаёт select. */
type GoalForm = {
  goal: LearningGoal | ''
  details: string
  deadline: string
  days: string
  minutes: string
}

const EMPTY_GOAL: GoalForm = { goal: '', details: '', deadline: '', days: '', minutes: '' }

/**
 * Редактирование анкеты ученика в модальном окне — как правка договора.
 *
 * Одно окно на всё, что ученик о себе сообщает: цель обучения с расписанием и
 * поля анкеты. Раньше их было два, с двумя кнопками «Редактировать» подряд, и
 * человек, поправив цель, закрывал окно, не догадавшись, что анкета — рядом.
 *
 * Уровня здесь нет: он измеряется тестом и правится учителем отдельно. Дай
 * править его тут — и профиль перестанет что-либо значить для программы
 * обучения, потому что каждый поставит себе желаемое.
 *
 * Состав полей анкеты и права приходят с бэкенда: ученику поля
 * `student_visible` доступны только для чтения, `teacher_only` не приходят.
 */
export function StudentFormEditDialog({
  open,
  contractId,
  userId,
  form,
  profile,
  canManage,
  onClose,
  onSaved,
}: StudentFormEditDialogProps) {
  const { t } = useTranslation()

  const [values, setValues] = useState<Record<string, StudentFormAnswerValue>>({})
  const [goal, setGoal] = useState<GoalForm>(EMPTY_GOAL)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  const initialGoal = useMemo<GoalForm>(
    () => ({
      goal: profile?.goal ?? '',
      details: profile?.goal_details ?? '',
      deadline: profile?.goal_deadline ?? '',
      days: profile?.days_per_week != null ? String(profile.days_per_week) : '',
      minutes: profile?.minutes_per_day != null ? String(profile.minutes_per_day) : '',
    }),
    [profile],
  )

  useEffect(() => {
    if (!open) return
    const map: Record<string, StudentFormAnswerValue> = {}
    for (const answer of form?.answers ?? []) {
      map[answer.field_key] = answer.value
    }
    setValues(map)
    setGoal(initialGoal)
    setError(null)
  }, [form, initialGoal, open])

  const fields = form?.fields ?? []
  // Ученику доступны для правки только student_editable; учителю — всё.
  const hasEditableFields = canManage
    ? fields.length > 0
    : fields.some((field) => field.access === 'student_editable')

  // Срок бывает не у всякой цели: у разговорной практики и «с нуля» его нет
  // по природе. Поле с экрана исчезает вместе с целью, а введённое значение
  // продолжало уезжать на сервер — и правка отбивалась ошибкой.
  const needsDeadline = goal.goal !== '' && GOALS_WITH_DEADLINE.includes(goal.goal)

  const goalChanged =
    goal.goal !== initialGoal.goal
    || goal.details !== initialGoal.details
    || goal.deadline !== initialGoal.deadline
    || goal.days !== initialGoal.days
    || goal.minutes !== initialGoal.minutes

  const handleSave = async () => {
    setSaving(true)
    setError(null)

    // Два независимых запроса: цель живёт в языковом профиле, ответы — в
    // анкете договора. Упавшая половина не отменяет успешную — иначе человек
    // теряет то, что уже сохранилось, и не понимает, что именно пропало.
    const saved: { form?: StudentForm; profile?: StudentLanguageProfile } = {}
    const failures: string[] = []

    if (goalChanged) {
      try {
        saved.profile = await languageProfileApi.updateGoal(userId, {
          goal: goal.goal === '' ? null : goal.goal,
          goal_details: goal.details.trim() === '' ? null : goal.details.trim(),
          goal_deadline: needsDeadline && goal.deadline !== '' ? goal.deadline : null,
          days_per_week: goal.days === '' ? null : Number(goal.days),
          minutes_per_day: goal.minutes === '' ? null : Number(goal.minutes),
        })
      } catch (err) {
        failures.push(displayErrorMessage(getErrorMessage(err), t('placement.goalSaveError')))
      }
    }

    if (hasEditableFields) {
      try {
        saved.form = await studentFormsApi.saveAnswers(contractId, values)
      } catch (err) {
        failures.push(displayErrorMessage(getErrorMessage(err), t('studentForms.saveError')))
      }
    }

    setSaving(false)

    // Сохранённое отдаём наверх в любом случае: карточка иначе покажет старое
    // рядом с ошибкой, и непонятно, что доехало.
    if (Object.keys(saved).length > 0) onSaved(saved)

    // Окно закрывается только при полном успехе: иначе ошибка исчезала бы
    // вместе с ним, и человек считал бы, что всё сохранилось.
    if (failures.length > 0) {
      setError(failures.join(' '))

      return
    }

    onClose()
  }

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>{t('studentForms.title')}</Box>
          {canManage && form?.exists && (
            <Button size="small" onClick={() => setHistoryOpen(true)}>
              {t('studentForms.history')}
            </Button>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 1.5 }}>
            {t('placement.goalSectionTitle')}
          </Typography>

          <Stack spacing={2}>
            <TextField
              select
              fullWidth
              size="small"
              label={t('placement.goal')}
              value={goal.goal}
              disabled={saving}
              onChange={(event) => {
                const next = event.target.value as LearningGoal | ''

                setGoal((current) => ({
                  ...current,
                  goal: next,
                  deadline:
                    next !== '' && GOALS_WITH_DEADLINE.includes(next) ? current.deadline : '',
                }))
              }}
            >
              <MenuItem value="">{t('placement.goalNotChosen')}</MenuItem>
              {LEARNING_GOALS.map((row) => (
                <MenuItem key={row} value={row}>
                  {t(`placement.goals.${row}`)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label={t('placement.goalDetails')}
              helperText={t('placement.goalDetailsHint')}
              value={goal.details}
              disabled={saving}
              onChange={(event) => setGoal((current) => ({ ...current, details: event.target.value }))}
            />

            {needsDeadline && (
              <TextField
                size="small"
                type="date"
                label={t('placement.goalDeadline')}
                slotProps={{ inputLabel: { shrink: true } }}
                value={goal.deadline}
                disabled={saving}
                onChange={(event) =>
                  setGoal((current) => ({ ...current, deadline: event.target.value }))
                }
                sx={{ maxWidth: 240 }}
              />
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                size="small"
                label={t('placement.daysPerWeek')}
                value={goal.days}
                disabled={saving}
                onChange={(event) => setGoal((current) => ({ ...current, days: event.target.value }))}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">{t('placement.notChosen')}</MenuItem>
                {[1, 2, 3, 4, 5, 6, 7].map((row) => (
                  <MenuItem key={row} value={String(row)}>
                    {row}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label={t('placement.minutesPerDay')}
                value={goal.minutes}
                disabled={saving}
                onChange={(event) =>
                  setGoal((current) => ({ ...current, minutes: event.target.value }))
                }
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">{t('placement.notChosen')}</MenuItem>
                {[15, 30, 45, 60, 90, 120].map((row) => (
                  <MenuItem key={row} value={String(row)}>
                    {row}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('studentForms.fieldsSectionTitle')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {fields.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('studentForms.noFields')}
              </Typography>
            ) : (
              fields.map((field) => (
                <StudentFormFieldInput
                  key={field.key}
                  field={field}
                  value={values[field.key] ?? null}
                  readOnly={saving || (!canManage && field.access !== 'student_editable')}
                  onChange={(next) => setValues((prev) => ({ ...prev, [field.key]: next }))}
                />
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" disabled={saving} onClick={() => void handleSave()}>
            {saving ? t('common.saving', 'Сохранение...') : t('common.save', 'Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>

      <StudentFormHistoryDialog
        open={historyOpen}
        contractId={contractId}
        onClose={() => setHistoryOpen(false)}
      />
    </>
  )
}
